import Card from "components/card";
import React, { useEffect, useState } from "react";

import { Combobox } from "@headlessui/react";
import { productTableApi, productTableXcelApi } from "api/dashboard";
import { getAllTcpsApi, getAllClientsApi2 } from "api/filtersData";
import { getAllCategoriesApi } from "api/category";
import { getAllFournisseursApi } from "api/fournisseur";
import { Spinner } from "@material-tailwind/react";
import PieChart from "components/charts/PieChart";
import BarChart from "components/charts/BarChart";
import API_URL from "config";

const ProductsTable = ({ accessList = {} }) => {
  const [comboBoxData, setComboBoxData] = useState([]);
  const [selectedComboBoxData, setSelectedComboBoxData] = useState("Tout");
  const [tcpData, setTcpData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [query, setQuery] = useState("");
  const [categoriesData, setCategoriesData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingXcel, setIsLoadingXcel] = useState(false);

  // category selected by clicking a pie slice
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedTcp, setSelectedTcp] = useState("all");
  const [fournisseursData, setFournisseursData] = useState([]);
  const [selectedFournisseur, setSelectedFournisseur] = useState("all");

  const filteredComboBoxData =
    query === ""
      ? comboBoxData
      : comboBoxData.filter((value) =>
          value.Name.toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  const getInitialData = () => {
    try {
      setIsLoading(true);

      if (fromDate && toDate) {
        productTableApi(
          selectedTcp,
          selectedComboBoxData,
          fromDate,
          toDate,
          '',
          selectedFournisseur
        )
        .then((productTableRes) => {
          setTableData(productTableRes);
        })
        .catch(err => {

        })
        .finally(() => {
          setIsLoading(false)
        })
      } else {
        productTableApi(
          selectedTcp,
          selectedComboBoxData,
          null,
          null,
          '',
          selectedFournisseur
        )
        .then((productTableRes) => {
          setTableData(productTableRes);
        })
        .catch(err => {

        })
        .finally(() => {
          setIsLoading(false)
        })
      }

      getAllTcpsApi()
        .then(allTcps => {
          setTcpData(allTcps);
        })
        .catch(err => {

        })

        getAllClientsApi2()
        .then(allClients => {
          setComboBoxData([
            {
              _id: null,
              Name: "Tout",
            },
            ...allClients,
          ]);
        })
        .catch(err => {

        })

        getAllCategoriesApi()
        .then(allCategories => {
          setCategoriesData(allCategories || []);
        })
        .catch(err => {
          console.log(err);
        })

        getAllFournisseursApi()
        .then(allFournisseurs => {
          setFournisseursData(allFournisseurs || []);
        })
        .catch(err => {
          console.log(err);
        })
    } catch (err) {
      console.log(err);
    }
  };

  // Prepare Top 10 products by amount (Montant HT)
  const getTopProductsBarData = () => {
    if (!tableData?.data) return { series: [], categories: [], productDetails: [] };

    const productTotals = [];

    // tableData.data: { [category]: { [productName]: [ ...entries, { sumOfAmount, sumOfQuantity } ] } }
    Object.entries(tableData.data).forEach(([category, products]) => {
      Object.entries(products).forEach(([productName, productData]) => {
        const amount = parseFloat(productData[productData.length - 1].sumOfAmount) || 0;
        const quantity = parseFloat(productData[productData.length - 1].sumOfQuantity) || 0;
        productTotals.push({ name: productName, amount, quantity });
      });
    });

    // Sort desc and take top 10
    const top10 = productTotals
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);

    return {
      series: [
        {
          name: 'Montant HT',
          data: top10.map((p) => p.amount)
        }
      ],
      categories: top10.map((p) => p.name),
      productDetails: top10
    };
  };

  const getBarChartOptions = () => {
    const { categories } = getTopProductsBarData();
    return {
      chart: {
        type: 'bar',
        toolbar: { show: false }
      },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          trim: true,
          style: {
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        labels: {
          formatter: (val) => formatNumber(val)
        },
        title: {
          text: 'Montant HT (DH)'
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
          borderRadius: 4
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${formatNumber(val)} DH`,
        style: {
          fontSize: '13px',
          fontWeight: 600,
          colors: ['#374151']
        },
        background: {
          enabled: true,
          borderRadius: 6,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: {
            enabled: false
          }
        },
        offsetY: -14
      },
      tooltip: {
        custom: function({series, seriesIndex, dataPointIndex, w}) {
          try {
            const { productDetails } = getTopProductsBarData();
            const product = productDetails[dataPointIndex];
            const grandTotal = getAllProductsTotalAmount();
            const percentage = grandTotal ? (product.amount / grandTotal) * 100 : 0;
            
            return '<div class="custom-tooltip" style="background: #000000; color: #ffffff; padding: 10px 15px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">' +
              '<div style="font-weight: 600; margin-bottom: 5px;">' + product.name + '</div>' +
              '<div style="font-size: 14px; margin-bottom: 3px;"><span style="color: #9CA3AF;">Quantité:</span> <span style="font-weight: bold;">' + formatNumber(product.quantity) + '</span></div>' +
              '<div style="font-size: 14px; margin-bottom: 3px;"><span style="color: #9CA3AF;">Montant HT:</span> <span style="font-weight: bold;">' + formatNumber(product.amount) + ' DH</span></div>' +
              '<div style="font-size: 14px; color: #9CA3AF; margin-top: 3px;">' + percentage.toFixed(1) + '% du total</div>' +
              '</div>';
          } catch (e) {
            return '<div class="custom-tooltip" style="background: #000000; color: #ffffff; padding: 10px 15px; border-radius: 8px;">' +
              '<div style="font-weight: 600;">' + formatNumber(series[seriesIndex][dataPointIndex]) + ' DH</div>' +
              '</div>';
          }
        }
      },
      colors: ['#236571']
    };
  };

  const getMinimalProductsBarData = () => {
    if (!tableData?.data) return { series: [], categories: [], productDetails: [] };

    const productTotals = [];

    // tableData.data: { [category]: { [productName]: [ ...entries, { sumOfAmount, sumOfQuantity } ] } }
    Object.entries(tableData.data).forEach(([category, products]) => {
      Object.entries(products).forEach(([productName, productData]) => {
        const amount = parseFloat(productData[productData.length - 1].sumOfAmount) || 0;
        const quantity = parseFloat(productData[productData.length - 1].sumOfQuantity) || 0;
        productTotals.push({ name: productName, amount, quantity });
      });
    });

    // Sort asc and take minimal 10
    const minimal10 = productTotals
      .sort((a, b) => a.amount - b.amount)
      .slice(0, 10);

    return {
      series: [
        {
          name: 'Montant HT',
          data: minimal10.map((p) => p.amount)
        }
      ],
      categories: minimal10.map((p) => p.name),
      productDetails: minimal10
    };
  };

  const getMinimalBarChartOptions = () => {
    const { categories } = getMinimalProductsBarData();
    return {
      chart: {
        type: 'bar',
        toolbar: { show: false }
      },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          trim: true,
          style: {
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        labels: {
          formatter: (val) => formatNumber(val)
        },
        title: {
          text: 'Montant HT (DH)'
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
          borderRadius: 4
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${formatNumber(val)} DH`,
        style: {
          fontSize: '13px',
          fontWeight: 600,
          colors: ['#374151']
        },
        background: {
          enabled: true,
          borderRadius: 6,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: {
            enabled: false
          }
        },
        offsetY: -14
      },
      tooltip: {
        custom: function({series, seriesIndex, dataPointIndex, w}) {
          try {
            const { productDetails } = getMinimalProductsBarData();
            const product = productDetails[dataPointIndex];
            const grandTotal = getAllProductsTotalAmount();
            const percentage = grandTotal ? (product.amount / grandTotal) * 100 : 0;
            
            return '<div class="custom-tooltip" style="background: #000000; color: #ffffff; padding: 10px 15px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">' +
              '<div style="font-weight: 600; margin-bottom: 5px;">' + product.name + '</div>' +
              '<div style="font-size: 14px; margin-bottom: 3px;"><span style="color: #9CA3AF;">Quantité:</span> <span style="font-weight: bold;">' + formatNumber(product.quantity) + '</span></div>' +
              '<div style="font-size: 14px; margin-bottom: 3px;"><span style="color: #9CA3AF;">Montant HT:</span> <span style="font-weight: bold;">' + formatNumber(product.amount) + ' DH</span></div>' +
              '<div style="font-size: 14px; color: #9CA3AF; margin-top: 3px;">' + percentage.toFixed(1) + '% du total</div>' +
              '</div>';
          } catch (e) {
            return '<div class="custom-tooltip" style="background: #000000; color: #ffffff; padding: 10px 15px; border-radius: 8px;">' +
              '<div style="font-weight: 600;">' + formatNumber(series[seriesIndex][dataPointIndex]) + ' DH</div>' +
              '</div>';
          }
        }
      },
      colors: ['#cf503f']
    };
  };

  const handleTcpSelectChange = (event) => {
    setSelectedTcp(event.target.value); // Update selected value in state
  };

  const handleFournisseurSelectChange = (event) => {
    setSelectedFournisseur(event.target.value);
  };

  const handleFilterData = () => {
    try {
      getInitialData();
    }
    catch (err) {

    }
  }

  const handleDownloadXcel = async () => {
    if (!accessList?.pdf_excel) return;
    try {
      setIsLoadingXcel(true);
      if (fromDate && toDate) {
        const productXcelTableRes = await productTableXcelApi(
          selectedTcp,
          selectedComboBoxData,
          fromDate,
          toDate,
          selectedFournisseur
        );

        window.open(`${API_URL}/${productXcelTableRes}`, "_blank");
      } else {
        const productXcelTableRes = await productTableXcelApi(
          selectedTcp,
          selectedComboBoxData,
          null,
          null,
          selectedFournisseur
        );

        window.open(`${API_URL}/${productXcelTableRes}`, "_blank");
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsLoadingXcel(false);
    }
  };

  // Format number with space as thousand separators
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-EN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Helper: grand total Montant HT across all products (used for percentage tooltips)
  const getAllProductsTotalAmount = () => {
    if (!tableData?.data) return 0;
    let total = 0;
    Object.entries(tableData.data).forEach(([category, products]) => {
      Object.entries(products).forEach(([productName, productData]) => {
        const amount = parseFloat(productData[productData.length - 1]?.sumOfAmount) || 0;
        total += amount;
      });
    });
    return total;
  };

  // Process data for pie chart by category
  const getPieChartData = () => {
    if (!tableData?.data) return { series: [], labels: [] };

    const categoryData = [];
    const categoryLabels = [];

    Object.entries(tableData.data).forEach(([category, products]) => {
      let categoryTotal = 0;
      
      Object.entries(products).forEach(([productName, productData]) => {
        const amount = parseFloat(productData[productData.length - 1].sumOfAmount);
        categoryTotal += amount;
      });

      if (categoryTotal > 0) {
        categoryData.push(categoryTotal);
        categoryLabels.push(category);
      }
    });

    return {
      series: categoryData,
      labels: categoryLabels
    };
  };

  // Normalize category name for matching (remove spaces, convert to lowercase)
  const normalizeCategoryName = (name) => {
    if (!name) return '';
    return name.toLowerCase().replace(/\s+/g, '').trim();
  };

  // Get colors for pie chart based on category colors
  const getCategoryColors = () => {
    const { labels } = getPieChartData();
    const defaultColors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', 
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    ];
    
    // Create a map of normalized category name to color
    const categoryColorMap = {};
    categoriesData.forEach(category => {
      if (category.Name && category.Color) {
        const normalizedName = normalizeCategoryName(category.Name);
        categoryColorMap[normalizedName] = category.Color;
      }
    });
    
    // Map each label to its category color, or use default color if not found
    return labels.map((label, index) => {
      const normalizedLabel = normalizeCategoryName(label);
      return categoryColorMap[normalizedLabel] || defaultColors[index % defaultColors.length];
    });
  };

  // Pie chart options
  const getPieChartOptions = () => {
    return {
      chart: {
        type: 'pie',
        height: 400,
        width: '100%',
        events: {
          dataPointSelection: function (event, chartContext, config) {
            const index = config.dataPointIndex;
            const series = config.w.config.series;
            const labels = config.w.config.labels;
            const value = series[index];
            const total = series.reduce((a, b) => a + b, 0);
            const percentage = total ? ((value / total) * 100) : 0;
            setSelectedCategory({
              name: labels[index],
              amount: value,
              percentage: percentage,
            });
          },
        },
      },
      labels: getPieChartData().labels,
      colors: getCategoryColors(),
      legend: {
        position: 'bottom',
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        horizontalAlign: 'center',
        width: '100%'
      },
      responsive: [{
        breakpoint: 768,
        options: {
          chart: {
            height: 350,
            width: '100%'
          },
          legend: {
            position: 'bottom',
            horizontalAlign: 'center'
          }
        }
      }, {
        breakpoint: 480,
        options: {
          chart: {
            height: 300,
            width: '100%'
          },
          legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '12px'
          }
        }
      }],
      tooltip: {
        enabled: true,
        theme: 'dark',
        style: {
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
          background: '#000000'
        },
        y: {
          formatter: function (val) {
            return formatNumber(val) + " DH";
          }
        },
        fillSeriesColor: false,
        custom: function({series, seriesIndex, dataPointIndex, w}) {
          const categoryName = w.globals.labels[seriesIndex];
          const value = series[seriesIndex];
          const total = series.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          
          return '<div class="custom-tooltip" style="background: #000000; color: #ffffff; padding: 10px 15px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">' +
            '<div style="font-weight: 600; margin-bottom: 5px;">' + categoryName + '</div>' +
            '<div style="font-size: 20px; font-weight: bold">' + formatNumber(value) + ' DH</div>' +
            '<div style="font-size: 18px; color: #9CA3AF; margin-top: 3px; font-weight: bold">' + percentage + '%</div>' +
            '</div>';
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val, opts) {
          const amount = formatNumber(opts.w.config.series[opts.seriesIndex]);
          return `${amount} DH\n(${val.toFixed(1)}%)`;
        },
        style: {
          fontSize: '20px',
          fontWeight: 500,
          colors: ['#111827']
        },
        background: {
          enabled: true,
          foreColor: '#FFFFFF',
          padding: 6,
          borderRadius: 8,
          borderWidth: 0,
          opacity: 0.92,
          dropShadow: {
            enabled: false
          }
        }
      },
      plotOptions: {
        pie: {
          expandOnClick: true,
          dataLabels: {
            offset: 0
          },
          donut: {
            size: '0%'
          }
        }
      }
    };
  };

  useEffect(() => {
    getInitialData();
  }, []); // selectedTcp, selectedComboBoxData, fromDate, toDate

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner className="h-12 w-12" />
        </div>
      ) : (
        <Card extra={"w-full pb-10 p-4 h-full"}>
          <header className="relative flex items-center justify-between">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              TABLE DES PRODUITS
            </div>
            {isLoadingXcel ? (
              <div className="flex items-center justify-center">
                <Spinner className="h-12 w-12" />
              </div>
            ) : accessList?.pdf_excel ? (
              <button
                href=""
                className="linear rounded-[10px] bg-green-500 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-green-400 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                onClick={handleDownloadXcel}
              >
                Télécharger excel
              </button>
            ) : null}
          </header>

          {/* Pie Chart Section */}
          {tableData?.data && Object.keys(tableData.data).length > 0 && (
            <div className="mt-5 mb-8">
              <Card extra={"w-full pb-10 p-4 h-full"}>
                <header className="relative flex items-center justify-between">
                  <div className="text-xl font-bold text-navy-700 dark:text-white">
                    Répartition par Catégorie
                  </div>
                </header>
                <div className="mt-4 w-full">
                  <PieChart
                    series={getPieChartData().series}
                    options={getPieChartOptions()}
                    width="100%"
                    height={420}
                  />
                </div>

                {selectedCategory ? (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-navy-700 dark:bg-navy-800">
                    <div className="text-lg font-bold text-navy-700 dark:text-white">
                      {selectedCategory.name}
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <small className="text-xs text-gray-600 dark:text-gray-300">
                          Montant HT
                        </small>
                        <p className="text-lg font-bold text-navy-700 dark:text-white font-mono">
                          {formatNumber(selectedCategory.amount)} DH
                        </p>
                      </div>
                      <div className="text-right">
                        <small className="text-xs text-gray-600 dark:text-gray-300">
                          Pourcentage
                        </small>
                        <p className="text-lg font-bold text-navy-700 dark:text-white font-mono">
                          {selectedCategory.percentage.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedCategory(null)}
                      className="text-xs font-semibold text-brand-500 hover:underline dark:text-brand-400"
                    >
                      Voir toutes les catégories
                    </button>
                  </div>
                ) : (
                  <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-navy-700 dark:bg-navy-800">
                    <div className="text-lg font-bold text-navy-700 dark:text-white">
                      Toutes les catégories
                    </div>
                    <div className="mt-3 overflow-x-scroll xl:overflow-x-hidden">
                      <table className="w-full">
                        <thead>
                          <tr>
                            <th className="border-b border-gray-200 pb-[10px] text-start dark:!border-navy-700">
                              <p className="text-xs tracking-wide text-gray-600">
                                Catégorie
                              </p>
                            </th>
                            <th className="border-b border-gray-200 pb-[10px] dark:!border-navy-700">
                              <p className="text-xs tracking-wide text-gray-600 text-right">
                                Montant HT
                              </p>
                            </th>
                            <th className="border-b border-gray-200 pb-[10px] dark:!border-navy-700">
                              <p className="text-xs tracking-wide text-gray-600 text-right">
                                Pourcentage
                              </p>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const { labels, series } = getPieChartData();
                            const total = series.reduce((a, b) => a + b, 0);
                            return labels.map((label, index) => {
                              const amount = series[index];
                              const percentage = total ? (amount / total) * 100 : 0;
                              return (
                                <tr
                                  key={label}
                                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-700"
                                  onClick={() =>
                                    setSelectedCategory({ name: label, amount, percentage })
                                  }
                                >
                                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                                      {label}
                                    </p>
                                  </td>
                                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                    <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                                      {formatNumber(amount)} DH
                                    </p>
                                  </td>
                                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                    <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                                      {percentage.toFixed(1)}%
                                    </p>
                                  </td>
                                </tr>
                              );
                            });
                          })()}
                          <tr className="bg-gray-200 dark:bg-brand-400">
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white">
                                Total
                              </p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                                {formatNumber(getAllProductsTotalAmount())} DH
                              </p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                                100%
                              </p>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Bar Chart Section - Top 10 Products by Montant HT */}
          {tableData?.data && Object.keys(tableData.data).length > 0 && (
            <div className="mt-5 mb-8">
              <Card extra={"w-full pb-10 p-4 h-full"}>
                <header className="relative flex items-center justify-between">
                  <div className="text-xl font-bold text-navy-700 dark:text-white">
                    Top 10 Produits par Montant HT
                  </div>
                </header>
                <div className="mt-4 w-full">
                  <BarChart
                    chartData={getTopProductsBarData().series}
                    chartOptions={getBarChartOptions()}
                    width="100%"
                    height={420}
                  />
                </div>
              </Card>
            </div>
          )}

          {/* Bar Chart Section - Minimal 10 Products by Montant HT */}
          {tableData?.data && Object.keys(tableData.data).length > 0 && (
            <div className="mt-5 mb-8">
              <Card extra={"w-full pb-10 p-4 h-full"}>
                <header className="relative flex items-center justify-between">
                  <div className="text-xl font-bold text-navy-700 dark:text-white">
                    Minimal 10 Produits par Montant HT
                  </div>
                </header>
                <div className="mt-4 w-full">
                  <BarChart
                    chartData={getMinimalProductsBarData().series}
                    chartOptions={getMinimalBarChartOptions()}
                    width="100%"
                    height={420}
                  />
                </div>
              </Card>
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-4">
            <div>
              <small className="text-sm font-medium text-gray-600 dark:text-white">
                Filtrer par date
              </small>
              <div>
                <input
                  type="date"
                  id=""
                  placeholder=""
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                />
                <input
                  type="date"
                  id=""
                  placeholder=""
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                />
              </div>
            </div>
            <div>
              <small className="text-sm font-medium text-gray-600 dark:text-white">
                Filtrer par TCP
              </small>
              <div>
                <select
                  label="select Version"
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  defaultValue="Tout"
                  onChange={handleTcpSelectChange}
                  value={selectedTcp}
                >
                  <option value="all">Tout</option>
                  {tcpData.map((el) => (
                    <option value={el._id}>{el.Name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <small className="text-sm font-medium text-gray-600 dark:text-white">
                Filtrer par client
              </small>
              <div>
                <Combobox
                  value={selectedComboBoxData}
                  onChange={setSelectedComboBoxData}
                >
                  <Combobox.Input
                    onChange={(event) => setQuery(event.target.value)}
                    // displayValue={(value) => value.name}
                    className="relative mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none"
                  />
                  <Combobox.Options className="absolute right-0 h-32 w-1/3 overflow-y-scroll bg-white">
                    {filteredComboBoxData.map((value) => (
                      <Combobox.Option
                        key={value._id}
                        value={value.Name}
                        className="cursor-pointer rounded-xl p-2 hover:bg-gray-100"
                      >
                        {value.Name}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                </Combobox>
              </div>
            </div>
            <div>
              <small className="text-sm font-medium text-gray-600 dark:text-white">
                Filtrer par Fournisseur
              </small>
              <div>
                <select
                  label="select Version"
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  defaultValue="all"
                  onChange={handleFournisseurSelectChange}
                  value={selectedFournisseur}
                >
                  <option value="all">Tout</option>
                  {fournisseursData.map((el) => (
                    <option key={el._id} value={el._id}>{el.Name}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
                href=""
                className="linear rounded-[10px] bg-green-500 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-green-400 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                onClick={handleFilterData}
              >
                Filtrer
              </button>
          </div>

          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            <table className="Products_table w-full">
              <thead>
                <tr>
                  <th className="TH1 border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600">
                      Catégorie
                    </p>
                  </th>
                  <th className="TH2 border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600">
                      Produit
                    </p>
                  </th>
                  <th className="TH3 border-b border-gray-200 pr-28 pb-[10px] dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600 text-right">Quantité</p>
                  </th>
                  <th className="TH4 border-b border-gray-200 pr-28 pb-[10px] dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600 text-right">
                      Montant HT
                    </p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData?.data
                  ? Object.entries(tableData?.data)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([key, value]) => {
                      let subQty = 0;
                      let subTotal = 0;
                      Object.entries(value).map(([index, product]) => {
                        subQty += parseFloat(product[product.length - 1].sumOfQuantity)
                        subTotal += parseFloat(product[product.length - 1].sumOfAmount)
                        // product.map((el) => {
                        //   subTotal += parseFloat(el.Amount);
                        //   subQty += parseFloat(el.Quantity);
                        // });
                      });
                      return (
                        <>
                          {Object.entries(value)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([key2, value2]) => {
                            let oneProductQty = 0;
                            let oneProductAmount = 0;

                            oneProductQty = value2[value2.length - 1].sumOfQuantity
                            oneProductAmount = value2[value2.length - 1].sumOfAmount

                            return (
                              <tr key={key2}>
                                <td className="TD1 pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                                    {key}
                                  </p>
                                </td>
                                <td className="TD2 pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                                    {key2}
                                  </p>
                                </td>
                                <td className="TD3 pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                                    {formatNumber(oneProductQty.toFixed(2))}
                                  </p>
                                </td>
                                <td className="TD4 pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                                    {formatNumber(oneProductAmount.toFixed(2))}
                                  </p>
                                </td>
                              </tr>
                            );
                          })}

                          <tr className="TR_color bg-gray-100 dark:bg-brand-400">
                            <td className="TD4 sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                            </td>
                            <td className="TD4 sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                            </td>
                            <td className="TD4 sm:text-[14px]">
                              <p className="text-sm text-[16px] font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                                {formatNumber(subQty.toFixed(2))}
                              </p>
                            </td>
                            <td className="TD4 sm:text-[14px]">
                              <p className="text-sm text-[16px] font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                                {formatNumber(subTotal.toFixed(2))}
                              </p>
                            </td>
                          </tr>
                        </>
                      );
                    })
                  : ""}
                <tr className="mt-5 bg-gray-200 dark:bg-brand-400">
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-[22px] text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                      {formatNumber(tableData?.sumOfQuantity?.toFixed(2))}
                    </p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-[22px] text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                      {formatNumber(tableData?.sumOfAmount?.toFixed(2))}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
};

export default ProductsTable;
